import { ElementType, FC, ReactNode, useState } from 'react';
import { Box, Slide, SlideProps, Snackbar, SxProps, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import { Alert } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { copyText } from '@shared/utils/utilsOld';
import UtmsButton from '@shared/components/ui/UtmsButton';

interface CopyToClipboardButtonProps {
    Component?: ElementType;
    textToCopy?: string;
    children?: ReactNode;
    sx?: SxProps;
    showButton?: boolean;
}

function TransitionDown(props: SlideProps) {
    return <Slide {...props} direction="down" />;
}

const CopyToClipboardButton: FC<CopyToClipboardButtonProps> = ({
    Component = 'div',
    textToCopy,
    children,
    sx,
    showButton,
    ...restProps
}) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const { t } = useTranslation();

    if (!navigator.clipboard) {
        return null; // do not show copy button, if there is no browser possibility to do copy
    }

    const handleCopyClick = async () => {
        await copyText(textToCopy);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <Box component={Component} sx={sx}>
            {
                // if children provided, do not show copy button
                children && showButton ? (
                    <UtmsButton
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'copy',
                            p: 0.5,
                            borderRadius: 1,
                            textTransform: 'unset',
                        }}
                        onClick={handleCopyClick}
                        endIcon={<ContentCopyIcon color="primary" sx={{ width: '20px', height: '20px' }} />}
                    >
                        <Typography color="primary">{children}</Typography>
                    </UtmsButton>
                ) : children && showButton === false ? (
                    <span style={{ cursor: 'copy' }} onClick={handleCopyClick}>
                        {children}
                    </span>
                ) : !children && textToCopy && showButton === undefined ? (
                    <IconButton color="default" {...restProps} onClick={handleCopyClick}>
                        <ContentCopyIcon />
                    </IconButton>
                ) : children && textToCopy?.length && textToCopy?.length > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <span>{children}</span>
                        <IconButton color="default" {...restProps} onClick={handleCopyClick}>
                            <ContentCopyIcon color="primary" />
                        </IconButton>
                    </Box>
                ) : null
            }
            <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={snackbarOpen}
                autoHideDuration={1000}
                onClose={handleSnackbarClose}
                TransitionComponent={TransitionDown}
            >
                <Alert severity="info" sx={{ width: '100%' }}>
                    {t('copied_to_clipboard')}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CopyToClipboardButton;
